import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Download, Upload, Plus, Trash2, Pencil, X, BarChart3, Table as TableIcon,
  ShoppingCart, Search,
} from "lucide-react";
import TableExportButton from "@/components/TableExportButton";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LabelList, Legend,
} from "recharts";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// 12-colour palette for per-customer / per-month series
const PALETTE = [
  "#1D2951", "#7C3AED", "#10B981", "#F59E0B", "#EF4444", "#3B82F6",
  "#EC4899", "#14B8A6", "#A855F7", "#F97316", "#06B6D4", "#84CC16",
];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const blankRow = {
  date: "",
  customer: "",
  part_no: "",
  part_name: "",
  drg_no: "",
  po_no: "",
  order_qty: "",
  part_rate: "",
  hsn_code: "",
  cgst_pct: "",
  sgst_pct: "",
  igst_pct: "",
  delivery_date: "",
};

export default function CustomerPORegister() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("table"); // 'table' | 'chart'
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState(""); // "" = All Companies
  const [companyOptions, setCompanyOptions] = useState([]);
  // Debounced search — avoids re-filtering ~2000 rows on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  // Chart filters
  const [chartMode, setChartMode] = useState("all"); // 'all' | 'month' | 'monthly' | 'custom'
  const [chartMonth, setChartMonth] = useState(todayISO().slice(0, 7)); // YYYY-MM
  const [chartFrom, setChartFrom] = useState("");
  const [chartTo, setChartTo] = useState("");
  const [chartCustomer, setChartCustomer] = useState(""); // empty = per-customer view; set = month-wise for that customer

  // Add / Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, or null for new
  const [form, setForm] = useState(blankRow);
  const [saving, setSaving] = useState(false);

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales/po-register");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Customer POs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/dashboard/companies").then((r) => setCompanyOptions(r.data || [])).catch(() => {});
  }, []);

  // ---------- Derived totals + filtered table rows ----------
  const filteredRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    let out = rows;
    if (companyFilter) {
      // STRICT company scope: only rows whose company_name matches.
      // Legacy untagged rows (no company_name) are excluded from a specific-company view.
      out = out.filter((r) => (r.company_name || "") === companyFilter);
    }
    if (!q) return out;
    return out.filter((r) =>
      [r.customer, r.part_no, r.part_name, r.drg_no, r.po_no]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, debouncedSearch, companyFilter]);

  const totals = useMemo(() => {
    let ordered = 0, value = 0, dispatched = 0, balance = 0;
    for (const r of filteredRows) {
      const oq = Number(r.order_qty || 0);
      const dq = Number(r.dispatched_qty || 0);
      const bq = Number(r.balance_qty || 0);
      const rate = Number(r.part_rate || 0);
      ordered += oq;
      dispatched += dq;
      balance += bq;
      value += oq * rate;
    }
    return { ordered, value, dispatched, balance };
  }, [filteredRows]);

  // ---------- Chart data (driven off delivery_date) ----------
  const chartRows = useMemo(() => {
    // Rows that actually have a delivery_date — needed for time-based modes
    const haveDate = (r) => !!(r.delivery_date && /^\d{4}-\d{2}-\d{2}$/.test(r.delivery_date));
    const inMonth = (r, ym) => r.delivery_date && r.delivery_date.startsWith(ym);
    const inRange = (r, from, to) => {
      if (!r.delivery_date) return false;
      if (from && r.delivery_date < from) return false;
      if (to && r.delivery_date > to) return false;
      return true;
    };
    let pool = rows;
    if (chartMode === "month") pool = rows.filter((r) => inMonth(r, chartMonth));
    if (chartMode === "custom") pool = rows.filter((r) => inRange(r, chartFrom, chartTo));
    if (chartMode === "monthly") pool = rows.filter(haveDate);
    if (chartCustomer) pool = pool.filter((r) => r.customer === chartCustomer);
    return pool;
  }, [rows, chartMode, chartMonth, chartFrom, chartTo, chartCustomer]);

  const customerOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.customer) s.add(r.customer);
    return Array.from(s).sort();
  }, [rows]);

  // For "monthly" mode with NO customer selected → stacked bars per month per top-N customers.
  // For "monthly" + customer set → simple bars per month for that customer.
  // For other modes → bars per customer.
  const customerSeries = useMemo(() => {
    if (chartMode === "monthly") return null;
    const map = new Map();
    for (const r of chartRows) {
      const v = Number(r.order_qty || 0) * Number(r.part_rate || 0);
      map.set(r.customer, (map.get(r.customer) || 0) + v);
    }
    const arr = Array.from(map.entries())
      .map(([customer, value]) => ({ customer, value, short: shortName(customer) }))
      .sort((a, b) => b.value - a.value);
    return arr;
  }, [chartRows, chartMode]);

  const monthlySeries = useMemo(() => {
    if (chartMode !== "monthly") return null;
    // bucket by YYYY-MM
    const byMonth = new Map();
    const topCustomers = topN(chartRows, chartCustomer ? 1 : 6);
    for (const r of chartRows) {
      if (!r.delivery_date) continue;
      const m = r.delivery_date.slice(0, 7);
      if (!byMonth.has(m)) byMonth.set(m, { month: m });
      const bucket = byMonth.get(m);
      const v = Number(r.order_qty || 0) * Number(r.part_rate || 0);
      const key = chartCustomer ? "Order Value" : (topCustomers.includes(r.customer) ? r.customer : "Other");
      bucket[key] = (bucket[key] || 0) + v;
    }
    const arr = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
    return { data: arr, series: chartCustomer ? ["Order Value"] : [...topCustomers, "Other"].filter((k) => arr.some((d) => d[k])) };
  }, [chartRows, chartMode, chartCustomer]);

  // ---------- Save / Delete ----------
  const openAdd = () => {
    setEditing(null);
    setForm({ ...blankRow, date: todayISO() });
    setEditOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date || todayISO(),
      customer: r.customer || "",
      part_no: r.part_no || "",
      part_name: r.part_name || "",
      drg_no: r.drg_no || "",
      po_no: r.po_no || "",
      order_qty: String(r.order_qty ?? ""),
      part_rate: String(r.part_rate ?? ""),
      hsn_code: r.hsn_code || "",
      cgst_pct: String(r.cgst_pct ?? ""),
      sgst_pct: String(r.sgst_pct ?? ""),
      igst_pct: String(r.igst_pct ?? ""),
      delivery_date: r.delivery_date || "",
    });
    setEditOpen(true);
  };
  const onSave = async () => {
    if (!form.customer.trim()) return toast.error("Customer is required");
    if (!form.part_no.trim()) return toast.error("Part No is required");
    if (!form.po_no.trim()) return toast.error("PO No is required");
    const payload = {
      ...form,
      order_qty: Number(form.order_qty || 0),
      part_rate: Number(form.part_rate || 0),
      cgst_pct: Number(form.cgst_pct || 0),
      sgst_pct: Number(form.sgst_pct || 0),
      igst_pct: Number(form.igst_pct || 0),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sales/po-register/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/sales/po-register", payload);
        toast.success("Added");
      }
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Delete ${r.po_no} · ${r.part_no} ?`)) return;
    try {
      await api.delete(`/sales/po-register/${r.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };
  const onDeleteAll = async () => {
    if (!window.confirm("Wipe ALL Customer POs in this workspace? This refuses if any invoice exists.")) return;
    try {
      const { data } = await api.delete("/sales/po-register/delete-all");
      toast.success(`Deleted ${data.deleted} POs`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Excel ----------
  const authedDownload = async (path, filename) => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const uploadExcel = async (file) => {
    setUploading(true);
    const t = toast.loading("Importing Customer POs…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sales/po-register/upload-excel", fd);
      const parts = [`Imported ${data.inserted}`];
      if (data.skipped?.length) parts.push(`${data.skipped.length} duplicates`);
      if (data.skipped_closed?.length) parts.push(`${data.skipped_closed.length} closed`);
      if (data.skipped_incomplete) parts.push(`${data.skipped_incomplete} incomplete rows`);
      if (data.header_row > 1) parts.push(`header on row ${data.header_row}`);
      toast.success(parts.join(" · "), { id: t });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed";
      toast.error(detail, { id: t, duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Sales</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Customer PO Register</h1>
          <p className="text-sm text-slate-500 mt-1">
            Master spreadsheet of every customer PO. Upload Excel to import in bulk · already-closed POs are skipped automatically on re-import.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1.5" data-testid="po-company-filter-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Company</span>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="po-company-filter"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none min-w-[140px]"
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <button onClick={openAdd} data-testid="add-entry-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus size={13} /> Add Entry
          </button>
          <button onClick={() => authedDownload("/sales/po-register/template", "customer_po_template.xlsx")}
            data-testid="template-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50">
            <Download size={13} /> Template
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" hidden
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadExcel(f); }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="import-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-50 text-white">
            <Upload size={13} /> {uploading ? "Importing…" : "Import"}
          </button>
          <button onClick={() => authedDownload(`/sales/po-register/export${companyFilter ? `?company=${encodeURIComponent(companyFilter)}` : ""}`, `sales_register_${(companyFilter || "all_companies").replace(/\s+/g, "_")}.xlsx`)}
            data-testid="export-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white">
            <Download size={13} /> Export Excel
          </button>
          <TableExportButton
            testIdPrefix="po-csv-export"
            filename={companyFilter ? `customer_po_register_${companyFilter.replace(/\s+/g, "_")}` : "customer_po_register"}
            rows={filteredRows}
            dateField="delivery_date"
            title={`Customer PO Register${companyFilter ? ` — ${companyFilter}` : ""}`}
            columns={[
              { key: "company_name", label: "Company" },
              { key: "customer", label: "Customer" },
              { key: "part_no", label: "Part No" },
              { key: "part_name", label: "Part Name" },
              { key: "drg_no", label: "Drg No" },
              { key: "part_rate", label: "Part Rate" },
              { key: "order_qty", label: "Order Qty" },
              { key: "order_value", label: "Order Value", format: (r) => Number(r.order_qty || 0) * Number(r.part_rate || 0) },
              { key: "po_no", label: "PO No" },
              { key: "delivery_date", label: "Delivery Date" },
              { key: "hsn_code", label: "HSN" },
              { key: "cgst_pct", label: "CGST %" },
              { key: "sgst_pct", label: "SGST %" },
              { key: "igst_pct", label: "IGST %" },
              { key: "dispatched_qty", label: "Dispatched" },
              { key: "balance_qty", label: "Balance" },
              { key: "status", label: "Status" },
            ]}
          />
          {isAdmin && (
            <button onClick={onDeleteAll} data-testid="delete-all-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 size={13} /> Delete All
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="border-b border-slate-100 px-4 pt-3 flex items-end gap-1">
          <TabBtn active={tab === "table"} onClick={() => setTab("table")} testid="tab-table">
            <TableIcon size={13} /> Table
          </TabBtn>
          <TabBtn active={tab === "chart"} onClick={() => setTab("chart")} testid="tab-chart">
            <BarChart3 size={13} /> Chart — Customer × Order Value
          </TabBtn>
          <div className="ml-auto pb-2 text-[11px] text-slate-500">
            <span className="font-mono">{rows.length}</span> orders
          </div>
        </div>

        {tab === "table" && (
          <div className="p-4 space-y-3">
            {/* Search + totals */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div className="min-w-[260px] flex-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Search</div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Customer / Part / PO…"
                    data-testid="po-search"
                    className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5 text-[11px]">
                <Stat label="Ordered" value={fmt(totals.ordered)} colour="text-slate-900" />
                <Stat label="Order Value" value={fmt(totals.value)} colour="text-violet-700" />
                <Stat label="Dispatched" value={fmt(totals.dispatched)} colour="text-emerald-700" />
                <Stat label="Balance" value={fmt(totals.balance)} colour="text-rose-700" />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-[12px]" data-testid="po-table">
                <thead className="bg-[#1D2951] text-white text-[11px] uppercase tracking-wider">
                  <tr>
                    <Th>Customer Name</Th>
                    <Th>Part No.</Th>
                    <Th>Part Name</Th>
                    <Th>Drg No.</Th>
                    <Th align="right">Part Rate</Th>
                    <Th align="right">Ordered Qty</Th>
                    <Th align="right">Order Value</Th>
                    <Th>PO No.</Th>
                    <Th>Delivery Date</Th>
                    <Th>HSN</Th>
                    <Th align="right">CGST %</Th>
                    <Th align="right">SGST %</Th>
                    <Th align="right">IGST %</Th>
                    <Th align="right" className="bg-emerald-600">Dispatch Qty</Th>
                    <Th align="right" className="bg-rose-600">Balance Qty</Th>
                    <Th align="center">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={16} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
                  )}
                  {!loading && filteredRows.length === 0 && (
                    <tr><td colSpan={16} className="px-3 py-8 text-center text-slate-400">
                      No customer POs yet. Use <span className="font-semibold">Add Entry</span> or <span className="font-semibold">Import</span> to load Excel data.
                    </td></tr>
                  )}
                  {filteredRows.map((r) => {
                    const value = Number(r.order_qty || 0) * Number(r.part_rate || 0);
                    const closed = (r.status || "").toLowerCase() === "closed";
                    return (
                      <tr key={r.id} className={closed ? "bg-slate-50" : "bg-yellow-50/40 hover:bg-yellow-50"}>
                        <Td>{r.customer}</Td>
                        <Td className="font-mono">{r.part_no}</Td>
                        <Td>{r.part_name}</Td>
                        <Td className="font-mono">{r.drg_no || "—"}</Td>
                        <Td align="right" className="font-mono">{fmt(r.part_rate)}</Td>
                        <Td align="right" className="font-mono font-semibold">{fmt(r.order_qty)}</Td>
                        <Td align="right" className="font-mono text-violet-700 font-semibold">{fmt(value)}</Td>
                        <Td className="font-mono">{r.po_no}</Td>
                        <Td className="font-mono">{r.delivery_date || "—"}</Td>
                        <Td className="font-mono">{r.hsn_code || "—"}</Td>
                        <Td align="right" className="font-mono">{Number(r.cgst_pct) ? `${Number(r.cgst_pct).toFixed(2)}%` : "—"}</Td>
                        <Td align="right" className="font-mono">{Number(r.sgst_pct) ? `${Number(r.sgst_pct).toFixed(2)}%` : "—"}</Td>
                        <Td align="right" className="font-mono">{Number(r.igst_pct) ? `${Number(r.igst_pct).toFixed(2)}%` : "—"}</Td>
                        <Td align="right" className="font-mono text-emerald-700">{Number(r.dispatched_qty) ? fmt(r.dispatched_qty) : "—"}</Td>
                        <Td align="right" className={`font-mono font-semibold ${Number(r.balance_qty) > 0 ? "text-rose-700" : "text-slate-400"}`}>{Number(r.balance_qty) > 0 ? fmt(r.balance_qty) : "—"}</Td>
                        <Td align="center">
                          <div className="inline-flex gap-1">
                            <button onClick={() => openEdit(r)} title="Edit" data-testid={`edit-btn-${r.id}`}
                              className="p-1 text-[#1D2951] hover:bg-slate-100 rounded">
                              <Pencil size={13} />
                            </button>
                            {isAdmin && (
                              <button onClick={() => onDelete(r)} title="Delete" data-testid={`delete-btn-${r.id}`}
                                className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "chart" && (
          <div className="p-4 space-y-4">
            {/* Chart filters */}
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Duration</div>
                <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-xs" data-testid="chart-mode">
                  {[
                    { k: "all", label: "All" },
                    { k: "month", label: "Month" },
                    { k: "monthly", label: "Monthly" },
                    { k: "custom", label: "Custom" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      onClick={() => setChartMode(o.k)}
                      data-testid={`chart-mode-${o.k}`}
                      className={`px-3 py-1.5 font-semibold ${chartMode === o.k ? "bg-[#1D2951] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {chartMode === "month" && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Month</div>
                  <input type="month" value={chartMonth} onChange={(e) => setChartMonth(e.target.value)}
                    data-testid="chart-month" className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
                </div>
              )}

              {chartMode === "custom" && (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">From</div>
                    <input type="date" value={chartFrom} onChange={(e) => setChartFrom(e.target.value)}
                      data-testid="chart-from" className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">To</div>
                    <input type="date" value={chartTo} onChange={(e) => setChartTo(e.target.value)}
                      data-testid="chart-to" className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
                  </div>
                </>
              )}

              <div className="min-w-[220px]">
                <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold mb-1">Customer {chartMode === "monthly" ? "(month-wise view)" : ""}</div>
                <select value={chartCustomer} onChange={(e) => setChartCustomer(e.target.value)}
                  data-testid="chart-customer"
                  className="border border-slate-300 rounded-md px-3 py-1.5 text-sm w-full">
                  <option value="">— None (per-customer view) —</option>
                  {customerOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Chart panel */}
            <div className="bg-white border-2 border-slate-900 rounded-md overflow-hidden">
              <div className="bg-[#1D2951] text-white px-4 py-2.5 flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <ShoppingCart size={14} />
                  {chartMode === "monthly"
                    ? (chartCustomer ? `${chartCustomer.toUpperCase()} — MONTHLY ORDER VALUE` : "MONTH-WISE ORDER VALUE BY TOP CUSTOMERS")
                    : `CUSTOMERS RANKED BY ORDER VALUE (${chartMode === "all" ? "ALL TIME" : chartMode === "month" ? `MONTH ${chartMonth}` : "CUSTOM RANGE"})`}
                </div>
                <div className="text-[11px] text-white/80 font-mono">
                  {chartMode === "monthly"
                    ? `${(monthlySeries?.data?.length || 0)} months`
                    : `${customerSeries?.length || 0} customers · Total ${fmtCompact(customerSeries?.reduce((s, x) => s + x.value, 0) || 0)}`}
                </div>
              </div>
              <div className="p-5">
                {chartMode !== "monthly" && (
                  customerSeries && customerSeries.length > 0 ? (
                    <ResponsiveContainer width="100%" height={420}>
                      <BarChart data={customerSeries} margin={{ top: 30, right: 10, left: 10, bottom: 60 }}>
                        <defs>
                          <linearGradient id="custBar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#7C3AED" stopOpacity={1} />
                            <stop offset="100%" stopColor="#1D2951" stopOpacity={1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="short" tick={{ fontSize: 12, fontWeight: 600 }} interval={0} angle={0} />
                        <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v) => [fmtCompact(v), "Order Value"]}
                          labelFormatter={(_, p) => p?.[0]?.payload?.customer || ""}
                          contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, fontSize: 12, color: "#fff" }}
                        />
                        <Bar dataKey="value" fill="url(#custBar)" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="value" position="top" formatter={fmtCompact} style={{ fontSize: 11, fontWeight: 600, fill: "#7C3AED" }} />
                          <LabelList position="insideBottom" content={({ x, y, width, index }) => (
                            <text x={x + width / 2} y={y - 6} fill="#F59E0B" fontSize={10} fontWeight={700} textAnchor="middle">#{index + 1}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No data for this selection.</div>
                  )
                )}
                {chartMode === "monthly" && (
                  monthlySeries && monthlySeries.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={420}>
                      <BarChart data={monthlySeries.data} margin={{ top: 30, right: 10, left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="month" tickFormatter={(m) => `${MONTH_LABELS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`} tick={{ fontSize: 11, fontWeight: 600 }} />
                        <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v, name) => [fmtCompact(v), name]}
                          contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, fontSize: 12, color: "#fff" }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {monthlySeries.series.map((k, i) => (
                          <Bar key={k} dataKey={k} stackId={chartCustomer ? undefined : "a"} fill={PALETTE[i % PALETTE.length]} radius={i === monthlySeries.series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No PO with a Delivery Date in the imported data.</div>
                  )
                )}
              </div>
            </div>

            {/* Helper text */}
            <div className="text-[11px] text-slate-500">
              All durations are read from the <span className="font-semibold">Delivery Date</span> column of each PO row. Use Customer dropdown to drill into one customer's monthly trend.
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {editOpen && (
        <Modal title={editing ? "Edit Customer PO" : "Add Customer PO"} onClose={() => setEditOpen(false)}>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Customer">
              <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })}
                data-testid="form-customer" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </Lbl>
            <Lbl label="PO No.">
              <input value={form.po_no} onChange={(e) => setForm({ ...form, po_no: e.target.value })}
                data-testid="form-po-no" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="Part No.">
              <input value={form.part_no} onChange={(e) => setForm({ ...form, part_no: e.target.value })}
                data-testid="form-part-no" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="Part Name">
              <input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })}
                data-testid="form-part-name" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </Lbl>
            <Lbl label="Drg No.">
              <input value={form.drg_no} onChange={(e) => setForm({ ...form, drg_no: e.target.value })}
                data-testid="form-drg-no" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="HSN Code">
              <input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })}
                data-testid="form-hsn-code"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="CGST %">
              <input type="number" min="0" max="100" step="any" value={form.cgst_pct}
                onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })}
                data-testid="form-cgst-pct"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="9" />
            </Lbl>
            <Lbl label="SGST %">
              <input type="number" min="0" max="100" step="any" value={form.sgst_pct}
                onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })}
                data-testid="form-sgst-pct"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="9" />
            </Lbl>
            <Lbl label="IGST %">
              <input type="number" min="0" max="100" step="any" value={form.igst_pct}
                onChange={(e) => setForm({ ...form, igst_pct: e.target.value })}
                data-testid="form-igst-pct"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="0" />
            </Lbl>
            <Lbl label="Ordered Qty">
              <input type="number" min="0" step="any" value={form.order_qty}
                onChange={(e) => setForm({ ...form, order_qty: e.target.value })}
                data-testid="form-order-qty" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="Part Rate (₹)">
              <input type="number" min="0" step="any" value={form.part_rate}
                onChange={(e) => setForm({ ...form, part_rate: e.target.value })}
                data-testid="form-part-rate" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
            </Lbl>
            <Lbl label="Delivery Date">
              <input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                data-testid="form-delivery-date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </Lbl>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              Estimated order value: <span className="font-mono font-semibold text-violet-700">₹{fmt(Number(form.order_qty || 0) * Number(form.part_rate || 0))}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditOpen(false)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={onSave} disabled={saving} data-testid="form-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md">
                {saving ? "Saving…" : (editing ? "Update" : "Add")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- Small components ----------
const Th = ({ children, align = "left", className = "" }) => (
  <th className={`px-3 py-2 text-${align} ${className}`}>{children}</th>
);
const Td = ({ children, align = "left", className = "" }) => (
  <td className={`px-3 py-2 border-t border-slate-100 align-top text-${align} ${className}`}>{children}</td>
);
const Stat = ({ label, value, colour }) => (
  <div className="text-right">
    <div className="text-slate-500 uppercase tracking-wider">{label}</div>
    <div className={`font-mono font-semibold ${colour} text-sm`}>{value}</div>
  </div>
);
const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 py-2 text-[12px] font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${active ? "border-amber-500 text-amber-700 bg-amber-50/40" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
    {children}
  </button>
);
const Lbl = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
    {children}
  </div>
);
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-slate-900">{title}</div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- helpers ----------
function shortName(name) {
  if (!name) return "—";
  return name.length > 12 ? `${name.slice(0, 10)}…` : name;
}
function topN(chartRows, n) {
  const m = new Map();
  for (const r of chartRows) {
    const v = Number(r.order_qty || 0) * Number(r.part_rate || 0);
    m.set(r.customer, (m.get(r.customer) || 0) + v);
  }
  return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, n).map((x) => x[0]);
}
